import React from "react";
import { Modal, ModalHeader, ModalFooter, ModalBody, Button } from "reactstrap";
import { ViewComponentPropType } from "@skyslit/ark-react";
import ExamModule from "..";
import ReactTable from "react-table";
import { Exam } from "lakshya-shared";
import moment from "moment";

type DataFilter = (exam: Exam, index: number) => boolean;

type PropType = {
  isOpen: boolean;
  toggle: any;
  onSelect: (exams: Exam[]) => void;
  isMultiSelect?: boolean;
  filter: DataFilter;
};

const PAGE_SIZE = 10;

export default function (
  props: ViewComponentPropType<ExamModule> & PropType
) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<Exam[]>([]);
  const [pages, setPages] = React.useState(-1);
  const [page, setPage] = React.useState(0);
  const [selectedIds, updateSelectedIds] = React.useState([]);
  const searchRef = React.useRef("");
  const fetchKeyRef = React.useRef("");
  const fetchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const dataFilter = props.filter || ((e: Exam, i: number) => true);

  const isMultiSelect: boolean = props.isMultiSelect
    ? props.isMultiSelect
    : false;

  const handleFetch = React.useCallback(
    (nextPage: number, nextSearch: string) => {
      const key = `${nextPage}::${nextSearch}`;
      fetchKeyRef.current = key;
      searchRef.current = nextSearch;
      setPage(nextPage);
      setIsLoading(true);

      props.module.services
        .getAllExam(null, {
          page: nextPage,
          limit: PAGE_SIZE,
          search: nextSearch,
        })
        .then((response: any) => {
          if (fetchKeyRef.current !== key) {
            return;
          }
          const exams = (response?.exams || []).filter(dataFilter);
          setData(
            exams.map((exam: any) => ({
              ...exam,
              createdAt: moment(exam.createdAt).format("ll"),
            }))
          );
          setPages(response?.pages || 1);
          setPage(response?.page ?? nextPage);
          setIsLoading(false);
        })
        .catch((err) => {
          if (fetchKeyRef.current !== key) {
            return;
          }
          console.error(err);
          setIsLoading(false);
        });
    },
    [props.module.services, dataFilter]
  );

  const onFetchData = (state: any) => {
    if (!props.isOpen) {
      return;
    }

    const titleFilter = (state.filtered || []).find(
      (f: any) => f.id === "title"
    );
    const nextSearch = titleFilter?.value
      ? String(titleFilter.value).trim()
      : "";
    const searchChanged = nextSearch !== searchRef.current;
    const nextPage = searchChanged
      ? 0
      : typeof state.page === "number"
      ? state.page
      : 0;
    const key = `${nextPage}::${nextSearch}`;

    if (fetchKeyRef.current === key) {
      return;
    }

    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    fetchTimeout.current = setTimeout(
      () => {
        handleFetch(nextPage, nextSearch);
      },
      searchChanged && nextSearch ? 400 : 0
    );
  };

  React.useEffect(() => {
    if (!props.isOpen) {
      fetchKeyRef.current = "";
      searchRef.current = "";
      setPage(0);
      setPages(-1);
      setData([]);
      updateSelectedIds([]);
    }
  }, [props.isOpen]);

  React.useEffect(() => {
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, []);

  const handleSelection = () => {
    props.onSelect &&
      props.onSelect(
        selectedIds.map((id) => {
          return data.find((e) => (e as any)._id === id);
        })
      );
    updateSelectedIds([]);
  };

  return (
    //@ts-ignore
    <Modal size="lg" isOpen={props.isOpen} toggle={props.toggle}>
      {/* @ts-ignore */}
      <ModalHeader toggle={props.toggle}>
        <i className="fas fa-scroll" /> Exam Picker
      </ModalHeader>
      {/* @ts-ignore */}
      <ModalBody>
        {props.isOpen ? (
          <ReactTable
            manual
            loading={isLoading}
            data={data}
            pages={pages}
            page={page}
            pageSize={PAGE_SIZE}
            minRows={5}
            showPageJump={true}
            defaultPageSize={PAGE_SIZE}
            pageSizeOptions={[PAGE_SIZE]}
            onFetchData={onFetchData}
            columns={[
              {
                Header: "",
                accessor: "_id",
                width: 50,
                filterable: false,
                Cell: (row) => {
                  const id = row.original._id;
                  return (
                    <input
                      type="checkbox"
                      checked={selectedIds.indexOf(id) > -1}
                      onChange={(e) => {
                        if (e.target.checked === true) {
                          if (isMultiSelect === true) {
                            updateSelectedIds([...selectedIds, id]);
                          } else {
                            updateSelectedIds([id]);
                          }
                        } else {
                          updateSelectedIds(
                            selectedIds.filter((_id) => _id !== id)
                          );
                        }
                      }}
                    />
                  );
                },
              },
              {
                Header: "Exams",
                filterable: true,
                accessor: "title",
              },
              {
                Header: "Status",
                accessor: "isPublished",
                filterable: false,
                Cell: (row) => {
                  const publishMessage: string = row.original.publishMessage;
                  const hasMessage: boolean =
                    publishMessage !== null && publishMessage !== "";
                  return (
                    <span
                      className={`${
                        row.value === true
                          ? "text-success"
                          : `${
                              hasMessage ? "text-warning" : "text-danger"
                            } font-weight-bold`
                      }`}
                    >
                      {row.value === true
                        ? "Published"
                        : hasMessage === true
                        ? publishMessage
                        : "Unpublished"}
                    </span>
                  );
                },
              },
              {
                Header: "Date Created",
                accessor: "createdAt",
                filterable: false,
              },
              {
                Header: "Template Type",
                accessor: "templateId",
                className: "lh-110 text-capitalize",
                filterable: false,
              },
              {
                Header: "Status",
                accessor: "status",
                filterable: false,
              },
            ]}
          />
        ) : null}
      </ModalBody>
      {/* @ts-ignore */}
      <ModalFooter>
        {/* @ts-ignore */}
        <Button color="secondary" onClick={props.toggle}>
          Cancel
        </Button>{" "}
        {/* @ts-ignore */}
        <Button
          disabled={selectedIds.length < 1}
          color="primary"
          onClick={handleSelection}
        >
          {selectedIds.length > 0
            ? `Select ${selectedIds.length} item(s)`
            : "Confirm Selection"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
